import { z } from 'zod';
import { timerProjectKindSchema } from './master-data.schema';

/**
 * Schnellstart der Zeiterfassung (VB-26027).
 *
 * Der Timer verlangt heute fünf Entscheidungen — Firma, Kategorie, Projekt,
 * Tätigkeit, Bemerkung —, und ein Wechsel weiter oben leert alles darunter. Wer
 * eine Aufzeichnung unterbricht und gleich weitermachen will, tippt alles noch
 * einmal. Beides hier setzt genau da an:
 *
 * - **Zuletzt verwendet** (`TimerRecentPreset`): aus den eigenen Einträgen
 *   abgeleitet, ohne Pflege, jüngste zuerst.
 * - **Favoriten** (`TimerFavoriteEntry`): selbst angelegt und benannt, in fester
 *   Reihenfolge.
 *
 * Beides liefert dieselbe startbereite Kombination (`TimerPreset`) — die
 * Oberfläche kann sie unverändert an `POST /time-tracking/start` weiterreichen.
 *
 * Nicht zu verwechseln mit `TimerFavorite` aus `master-data.schema.ts`: Das ist
 * der ältere Favorit auf der reinen PROJEKT-Ebene im Auswahl-Popup. Er bleibt.
 */

/**
 * Eine startbereite Kombination — mit aufgelösten Namen, damit Listen ohne
 * weitere Abfragen darstellbar sind.
 */
export const timerPresetSchema = z.object({
  legalEntityId: z.string().uuid(),
  legalEntityName: z.string(),
  categoryId: z.string().uuid(),
  categoryName: z.string(),
  projectKind: timerProjectKindSchema,
  projectId: z.string().uuid(),
  projectName: z.string(),
  /** Projekt-Code, sofern vorhanden (echte ProERP-Projekte). */
  projectCode: z.string().nullable(),
  /** Tätigkeit ist auch im Timer optional — hier entsprechend nullable. */
  ttSubCategoryId: z.string().uuid().nullable(),
  ttSubCategoryName: z.string().nullable(),
  note: z.string().nullable(),
});
export type TimerPreset = z.infer<typeof timerPresetSchema>;

/**
 * Eine Zeile „Zuletzt verwendet".
 *
 * Zusammengefasst wird **inklusive Bemerkung**: Die Kachel zeigt damit genau
 * das, was beim Klick aufgezeichnet wird. Ließe man die Bemerkung aus dem
 * Schlüssel heraus, stünde in der Kachel eine Bemerkung, die zu einer anderen
 * Aufzeichnung gehörte — und der Melder hat die Notizen ausdrücklich als Teil
 * dessen genannt, was er jedes Mal neu tippen muss.
 */
export const timerRecentPresetSchema = timerPresetSchema.extend({
  /** Stabiler Schlüssel der Kombination — die Oberfläche vergleicht damit gegen Favoriten. */
  key: z.string(),
  lastUsedAt: z.string().datetime(),
});
export type TimerRecentPreset = z.infer<typeof timerRecentPresetSchema>;

/** Ein gespeicherter Favorit je Benutzer. */
export const timerFavoriteEntrySchema = timerPresetSchema.extend({
  id: z.string().uuid(),
  /** Selbst vergebener Name — das ist die Beschriftung der Kachel. */
  name: z.string(),
  sortOrder: z.number().int(),
  key: z.string(),
  /** Wie oft der Benutzer diese Kombination bisher aufgezeichnet hat. */
  usageCount: z.number().int(),
  /**
   * true, wenn Firma, Kategorie, Projekt oder Tätigkeit inzwischen fehlen oder
   * inaktiv sind. Geprüft wird in der Reihenfolge der Timer-Kaskade, gemeldet
   * der erste Befund. Der Favorit bleibt sichtbar, lässt sich aber nicht starten
   * — sonst liefe ein Klick in einen Serverfehler, und niemand wüsste warum.
   *
   * Einschränkung: Bei echten ProERP-Projekten wird nur „gibt es gar nicht mehr"
   * erkannt. Ein abgeschlossenes oder archiviertes Projekt bleibt unauffällig,
   * weil die Public API des Projekt-Moduls (§5) beim Massen-Abruf keinen Status
   * liefert. Bei Zeit-Projekten wird der Status geprüft.
   */
  isStale: z.boolean(),
  /** Klartext, was fehlt („Das Projekt gibt es nicht mehr.") — nur wenn `isStale`. */
  staleReason: z.string().nullable(),
});
export type TimerFavoriteEntry = z.infer<typeof timerFavoriteEntrySchema>;

/**
 * Favorit anlegen. Die Bemerkung gehört bewusst NICHT dazu: Sie beschreibt die
 * einzelne Aufzeichnung („Inbetriebnahme Linie 2"), nicht die wiederkehrende
 * Tätigkeit. Ein Favorit mit eingebrannter Bemerkung wäre nach einer Woche falsch.
 */
export const createTimerFavoriteEntrySchema = z.object({
  name: z.string().trim().min(1, 'Bitte einen Namen vergeben.').max(80),
  legalEntityId: z.string().uuid(),
  categoryId: z.string().uuid(),
  projectKind: timerProjectKindSchema,
  projectId: z.string().uuid(),
  ttSubCategoryId: z.string().uuid().nullable().optional(),
});
export type CreateTimerFavoriteEntryInput = z.infer<typeof createTimerFavoriteEntrySchema>;

/** Umbenennen. Die Kombination selbst ist unveränderlich — dafür löscht man neu an. */
export const updateTimerFavoriteEntrySchema = z.object({
  name: z.string().trim().min(1, 'Bitte einen Namen vergeben.').max(80),
});
export type UpdateTimerFavoriteEntryInput = z.infer<typeof updateTimerFavoriteEntrySchema>;

/** Reihenfolge festlegen — die IDs in der gewünschten Abfolge. */
export const reorderTimerFavoriteEntriesSchema = z.object({
  ids: z.array(z.string().uuid()).min(1),
});
export type ReorderTimerFavoriteEntriesInput = z.infer<typeof reorderTimerFavoriteEntriesSchema>;

/** Abfrage der Verlaufsliste. */
export const timerRecentPresetsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(12).default(6),
});
export type TimerRecentPresetsQuery = z.infer<typeof timerRecentPresetsQuerySchema>;

/**
 * Schlüssel einer Kombination. Eine Funktion, damit Server (Zusammenfassen des
 * Verlaufs, Doppelten-Prüfung bei Favoriten) und Oberfläche (Abgleich Verlauf ↔
 * Favoriten) garantiert dasselbe rechnen.
 *
 * `note` fließt nur in den Verlaufs-Schlüssel ein; Favoriten tragen keine
 * Bemerkung und übergeben deshalb nichts.
 */
export function timerPresetKey(parts: {
  legalEntityId: string;
  categoryId: string;
  projectKind: string;
  projectId: string;
  ttSubCategoryId?: string | null;
  note?: string | null;
}): string {
  return [
    parts.legalEntityId,
    parts.categoryId,
    parts.projectKind,
    parts.projectId,
    parts.ttSubCategoryId ?? '',
    (parts.note ?? '').trim(),
  ].join('|');
}

/**
 * Beschriftung einer Kombination in einer Zeile — überall gleich, damit Kachel,
 * Liste und laufender Timer nicht auseinanderlaufen.
 *
 * Vorn steht, was die Zeile unterscheidbar macht: die Tätigkeit, sonst die
 * Bemerkung, sonst das Projekt. „Montage" allein sagt mehr als
 * „P-26116090 · Kläranlage Nord", wenn darunter ohnehin das Projekt steht.
 */
export function timerPresetTitle(preset: {
  ttSubCategoryName: string | null;
  note: string | null;
  projectName: string;
}): string {
  if (preset.ttSubCategoryName) {
    return preset.note ? `${preset.ttSubCategoryName} · ${preset.note}` : preset.ttSubCategoryName;
  }
  return preset.note ?? preset.projectName;
}

/** Zweite Zeile: wohin gebucht wird. */
export function timerPresetSubtitle(preset: {
  legalEntityName: string;
  categoryName: string;
  projectName: string;
  projectCode: string | null;
}): string {
  const project = preset.projectCode ? `${preset.projectCode} · ${preset.projectName}` : preset.projectName;
  return `${preset.legalEntityName} · ${preset.categoryName} · ${project}`;
}
